import { useEffect, useRef, useState } from "react";

/**
 * useScrollZoom — returns a CSS transform string driven by scroll position.
 * Element subtly scales between [minScale, maxScale] across the viewport.
 * Very gentle "magnifier" effect — meant to be barely noticeable.
 */
export const useScrollZoom = ({
    min = 0.97,
    max = 1.04,
    peak = 0.45, // viewport progress at which element is at maxScale
} = {}) => {
    const ref = useRef(null);
    const [transform, setTransform] = useState(`scale(${min})`);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;

        let raf = 0;
        const compute = () => {
            const rect = el.getBoundingClientRect();
            const vh = window.innerHeight || 1;
            // progress: 0 when element top is at bottom of viewport, 1 when at top
            const progress = Math.min(
                1,
                Math.max(0, 1 - (rect.top + rect.height / 2) / vh + 0.5)
            );
            // bell curve around `peak` so it grows in and out subtly
            const dist = Math.abs(progress - peak) / Math.max(peak, 1 - peak);
            const t = 1 - Math.min(1, dist);
            const scale = min + (max - min) * t;
            setTransform(`scale(${scale.toFixed(4)})`);
        };

        const onScroll = () => {
            if (raf) return;
            raf = requestAnimationFrame(() => {
                raf = 0;
                compute();
            });
        };

        compute();
        window.addEventListener("scroll", onScroll, { passive: true });
        window.addEventListener("resize", onScroll);
        return () => {
            window.removeEventListener("scroll", onScroll);
            window.removeEventListener("resize", onScroll);
            if (raf) cancelAnimationFrame(raf);
        };
    }, [min, max, peak]);

    return { ref, style: { transform, transition: "transform 200ms cubic-bezier(0.22, 1, 0.36, 1)", willChange: "transform" } };
};

export default useScrollZoom;
